/*
 * STEELVA storefront translations.
 *
 * Why this file exists:
 *  1. Shopify renders the dynamic checkout button ("Buy it now", Shop Pay,
 *     Apple/Google Pay) server-side via `{{ form | payment_button }}`. The
 *     label is locale-dependent on Shopify's side, so when the merchant's
 *     storefront default locale isn't Arabic, it appears in English even
 *     though the theme is RTL.
 *  2. Variant option names (Size, Color, Material) come from the merchant's
 *     product data and need to be re-labeled to Arabic without rewriting
 *     every product in Shopify admin.
 *
 * This script runs once at DOMContentLoaded, then a MutationObserver keeps
 * the labels Arabic as Shopify hydrates async content.
 */

(function () {
  'use strict';

  // English option name -> Arabic. Case-insensitive match.
  const OPTION_NAMES = {
    'size': 'المقاس',
    'color': 'اللون',
    'colour': 'اللون',
    'material': 'الخامة',
    'style': 'الستايل',
    'fit': 'القَصّة',
    'pattern': 'النقشة',
    'length': 'الطول',
    'width': 'العرض',
    'height': 'الارتفاع',
    'weight': 'الوزن',
    'flavor': 'النكهة',
    'flavour': 'النكهة',
    'scent': 'الرائحة',
    'design': 'التصميم',
    'finish': 'اللمسة النهائية',
    'title': 'الخيار',
  };

  // English variant value -> Arabic. Used for sizes, colors that aren't
  // translated in the merchant's product data. Case-insensitive.
  const OPTION_VALUES = {
    'small': 'صغير',
    'medium': 'وسط',
    'large': 'كبير',
    'extra small': 'صغير جدًا',
    'extra large': 'كبير جدًا',
    'xs': 'XS',
    's': 'S',
    'm': 'M',
    'l': 'L',
    'xl': 'XL',
    'xxl': 'XXL',
    'one size': 'مقاس واحد',
    'default title': 'الخيار الافتراضي',
  };

  // Strings appearing inside the Shopify-rendered dynamic checkout button.
  // We match prefix words so "Buy it now" / "Buy with G Pay" all work.
  const PAYMENT_BUTTON_TEXT = [
    { match: /^\s*Buy it now\s*$/i, replace: 'اشترِ الآن' },
    { match: /^\s*Buy now\s*$/i, replace: 'اشترِ الآن' },
    { match: /^\s*Buy with\s+/i, replace: 'اشترِ عبر ' },
    { match: /^\s*Pay with\s+/i, replace: 'ادفع عبر ' },
    { match: /^\s*More payment options\s*$/i, replace: 'خيارات دفع أخرى' },
  ];

  function translateOptionName(node) {
    const raw = (node.textContent || '').trim();
    if (!raw) return;
    const key = raw.toLowerCase();
    if (OPTION_NAMES[key]) {
      // Preserve any trailing colon / whitespace the theme adds.
      const trailing = node.textContent.match(/[\s:]+$/)?.[0] || '';
      node.textContent = OPTION_NAMES[key] + trailing;
    }
  }

  function translateOptionValue(node) {
    const raw = (node.textContent || '').trim();
    if (!raw) return;
    const key = raw.toLowerCase();
    if (OPTION_VALUES[key]) {
      node.textContent = OPTION_VALUES[key];
    }
  }

  function translatePaymentButton(buttonText) {
    // Walk text nodes only — preserves icons / nested spans inside the button.
    const walker = document.createTreeWalker(buttonText, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const raw = node.nodeValue;
      if (!raw || !raw.trim()) continue;
      for (const rule of PAYMENT_BUTTON_TEXT) {
        if (rule.match.test(raw)) {
          node.nodeValue = raw.replace(rule.match, rule.replace);
          break;
        }
      }
    }
  }

  function applyTranslations(root) {
    const scope = root || document;

    // Variant option NAMES — rendered inside <legend> in the variant picker
    // and the dropdown <label>. The theme uses .variant-option fieldsets.
    scope.querySelectorAll('.variant-option legend').forEach((el) => {
      // The legend may contain a swatch value span; only translate its
      // first text node (the option name).
      const first = Array.from(el.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim()
      );
      if (first) {
        const key = first.nodeValue.trim().toLowerCase();
        if (OPTION_NAMES[key]) {
          first.nodeValue = first.nodeValue.replace(
            new RegExp(first.nodeValue.trim(), 'i'),
            OPTION_NAMES[key]
          );
        }
      }
    });

    scope
      .querySelectorAll('.variant-option[data-testid="variant-option-single"]')
      .forEach((el) => {
        const first = Array.from(el.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim()
        );
        if (first) {
          const key = first.nodeValue.trim().toLowerCase();
          if (OPTION_NAMES[key]) {
            first.nodeValue = first.nodeValue.replace(
              new RegExp(first.nodeValue.trim(), 'i'),
              OPTION_NAMES[key]
            );
          }
        }
      });

    // Dropdown label (used when variant_style is select)
    scope.querySelectorAll('label[for^="Option-"]').forEach(translateOptionName);

    // Variant option VALUES (Small/Medium/Large pills, swatch labels)
    scope
      .querySelectorAll(
        '.variant-option__button-label__text, .variant-option__swatch-value'
      )
      .forEach(translateOptionValue);

    // Shopify dynamic checkout button — the rendered markup uses
    // .shopify-payment-button and contains nested <button>s.
    scope
      .querySelectorAll('.shopify-payment-button button')
      .forEach(translatePaymentButton);
  }

  function init() {
    applyTranslations(document);

    // Re-run when variant pickers swap, when Shopify mounts payment-button,
    // and when quick-add / cart updates re-render details.
    const observer = new MutationObserver((mutations) => {
      let touched = false;
      for (const m of mutations) {
        if (m.type === 'childList' && m.addedNodes.length) {
          touched = true;
          break;
        }
      }
      if (touched) applyTranslations(document);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
